import { Injectable, NgZone } from '@angular/core';
import { doc, collection, serverTimestamp, setDoc, getDoc, query, startAfter, limit, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { DocumentData, QueryDocumentSnapshot, Unsubscribe } from 'firebase/firestore';
import { BehaviorSubject } from 'rxjs';

import { DataStoreService } from 'src/app/services/data-store/data-store.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';

export class AdManagedOrganizationError extends Error {
  constructor() {
    super('This organization is managed by Entra ID. Member changes must be made in Azure Portal.');
    this.name = 'AdManagedOrganizationError';
  }
}

export enum OrganizationRole {
 Admin = 'admin',
 Member = 'member',
 ChatUser = 'chatUser',
 ContentManager = 'contentManager',
}

export type OrganizationMembersPage = {
  members: OrganizationMember[];
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
};

export type OrganizationMember = {
  userId: string;
  role: OrganizationRole;
  roleId?: string;
  permissions: string[];
  joinedAt: any;
};

export type MemberDetails = { [id: string]: { email: string, displayName: string }}

@Injectable({
  providedIn: 'root'
})
export class OrganizationMemberService {
  /** In-memory cache: survives navigation within the same tab session. */
  private readonly cache = new Map<string, OrganizationMember>();

  /** Real-time stream of the current user's member document. Emits null when the member doc is deleted. */
  readonly currentMember$ = new BehaviorSubject<OrganizationMember | null>(null);

  private memberUnsubscribe: Unsubscribe | null = null;
  private watchedOrgId: string | null = null;

  constructor(
    private dataStore: DataStoreService,
    private firebase: FirebaseService,
    private ngZone: NgZone,
    private organizationService: OrganizationService,
  ) {
    this.dataStore.getState$().subscribe(({ newState, oldState }) => {
      const newOrgId = newState.currentAccount?.organizationId;
      const oldOrgId = oldState?.currentAccount?.organizationId;

      if (!newOrgId) {
        this.stopWatchingMember();
        return;
      }

      if (newOrgId !== oldOrgId || newOrgId !== this.watchedOrgId) {
        this.watchCurrentMember(newOrgId);
      }
    });
  }

  private watchCurrentMember(organizationId: string): void {
    this.stopWatchingMember();
    this.watchedOrgId = organizationId;

    const userId = this.firebase.auth.currentUser?.uid;
    if (!userId) return;

    const memberRef = doc(this.firebase.firestore, 'organizations', organizationId, 'members', userId);

    this.memberUnsubscribe = onSnapshot(memberRef, (snapshot) => {
      this.ngZone.run(() => {
        if (snapshot.exists()) {
          const member = this.mapMemberData(snapshot.data());
          // Keep caches in sync so one-shot reads stay fresh
          const cacheKey = `crewdle_member_${organizationId}_${userId}`;
          this.cache.set(cacheKey, member);
          sessionStorage.setItem(cacheKey, JSON.stringify(member));
          this.currentMember$.next(member);
        } else {
          // Member doc deleted — user was removed from the org
          const cacheKey = `crewdle_member_${organizationId}_${userId}`;
          this.cache.delete(cacheKey);
          sessionStorage.removeItem(cacheKey);
          this.currentMember$.next(null);
        }
      });
    }, (error) => {
      console.error('Member snapshot error:', error);
    });
  }

  private stopWatchingMember(): void {
    if (this.memberUnsubscribe) {
      this.memberUnsubscribe();
      this.memberUnsubscribe = null;
    }
    this.watchedOrgId = null;
  }

  async getCurrentMember(organizationId: string): Promise<OrganizationMember> {
    if (!this.firebase.auth.currentUser) {
      throw new Error('User not logged in');
    }

    const userId = this.firebase.auth.currentUser.uid;
    const cacheKey = `crewdle_member_${organizationId}_${userId}`;

    // 1. In-memory hit (fastest — survives angular navigation)
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // 2. sessionStorage hit (survives page refresh within the same tab)
    const stored = sessionStorage.getItem(cacheKey);
    if (stored) {
      const member = JSON.parse(stored) as OrganizationMember;
      this.cache.set(cacheKey, member);
      return member;
    }

    // 3. Firestore fetch — cache the result for the rest of the session
    const member = await this.getOrganizationMember(organizationId, userId);
    this.cache.set(cacheKey, member);
    sessionStorage.setItem(cacheKey, JSON.stringify(member));
    return member;
  }

  /** Call this after updating a member's role/permissions so the cache stays fresh. */
  invalidateMemberCache(organizationId: string, userId: string): void {
    const cacheKey = `crewdle_member_${organizationId}_${userId}`;
    this.cache.delete(cacheKey);
    sessionStorage.removeItem(cacheKey);
  }

  /** Clear all crewdle permission caches from sessionStorage (e.g. after an admin saves changes). */
  static clearPermissionCaches(): void {
    const keysToRemove = Object.keys(sessionStorage).filter(k => k.startsWith('crewdle_'));
    keysToRemove.forEach(k => sessionStorage.removeItem(k));
  }

  async getOrganizationMembers(organizationId: string, limitPerPage: number, lastDoc?: QueryDocumentSnapshot<DocumentData> | null): Promise<OrganizationMembersPage> {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    const membersRef = collection(organizationRef, 'members');

    let memberQuery = query(membersRef, limit(limitPerPage));
    if (lastDoc) {
      memberQuery = query(memberQuery, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(memberQuery);
    const members = querySnapshot.docs.map(doc => this.mapMemberData(doc.data()));

    if (querySnapshot.size < limitPerPage) {
      return { members, lastVisible: null };
    }

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return { members, lastVisible };
  }


  async listMembersDetailsOfOrganization(organizationId: string): Promise<MemberDetails> {
    const { functions } = this.firebase;

    const listMembersDetailsOfOrganizationCallable = httpsCallable<{ organizationId: string }, { users: MemberDetails }>(functions, 'listMembersDetailsOfOrganization');

    try {
      const result = await listMembersDetailsOfOrganizationCallable({
        organizationId,
      });

      return result.data.users;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw new Error('Error fetching user details');
    }
  }

  async getOrganizationMember(organizationId: string, memberId: string): Promise<OrganizationMember> {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    const memberRef = doc(organizationRef, 'members', memberId);
    const memberSnapshot = await getDoc(memberRef);

    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    return this.mapMemberData(memberSnapshot.data());
  }

  async isAdManaged(organizationId: string): Promise<boolean> {
    const org = await this.organizationService.getOrganization(organizationId);
    return !!org.tid;
  }

  async addMemberToOrganization(organizationId: string, role: OrganizationRole, userId?: string) {
    if (await this.isAdManaged(organizationId)) {
      throw new AdManagedOrganizationError();
    }

    if (!this.firebase.auth.currentUser) {
      throw new Error('User not logged in');
    }

    if (!userId) {
      userId = this.firebase.auth.currentUser.uid;
    }

    const db = this.firebase.firestore;
    const organizationRef = doc(db, 'organizations', organizationId);
    const memberRef = doc(organizationRef, 'members', this.firebase.auth.currentUser.uid);

    setDoc(memberRef, {
      userId,
      role,
      joinedAt: serverTimestamp(),
    });

    this.organizationService.addOrganizationToUser(this.firebase.auth.currentUser.uid, organizationId);
  }

  async updateMemberRole(organizationId: string, memberId: string, role: OrganizationRole) {
    if (await this.isAdManaged(organizationId)) {
      throw new AdManagedOrganizationError();
    }

    const isAdmin = await this.isCurrentUserAdmin(organizationId);
    if (!isAdmin) {
      throw new Error('Only organization admins can update members');
    }

    const db = this.firebase.firestore;
    const organizationRef = doc(db, 'organizations', organizationId);
    const memberRef = doc(organizationRef, 'members', memberId);

    await setDoc(memberRef, { role }, { merge: true });
  }

  async removeMemberFromOrganization(organizationId: string, memberId: string) {
    if (await this.isAdManaged(organizationId)) {
      throw new AdManagedOrganizationError();
    }

    if (memberId === this.firebase.auth.currentUser?.uid) {
      throw new Error('Cannot remove yourself from the organization');
    }

    const isAdmin = await this.isCurrentUserAdmin(organizationId);
    if (!isAdmin) {
      throw new Error('Only organization admins can remove members');
    }

    return this.removeMemberFromOrganizationFunction(organizationId, memberId);
  }

  async isCurrentUserAdmin(organizationId: string): Promise<boolean> {
    if (!this.firebase.auth.currentUser) {
      return false;
    }

    const member = await this.getOrganizationMember(organizationId, this.firebase.auth.currentUser.uid);
    return member.role === OrganizationRole.Admin;
  }

  // TODO - rewrite this using converters
  private mapMemberData(memberData?: DocumentData): OrganizationMember {
    if (!memberData) {
      throw new Error('Member data is required');
    }

    return {
      userId: memberData['userId'],
      role: memberData['role'],
      roleId: memberData['roleId'],
      permissions: memberData['permissions'],
      joinedAt: memberData['joinedAt'],
    };
  }

  async removeMemberFromOrganizationFunction(organizationId: string, userId: string) {
    const removeUserFromOrganizationCallable = httpsCallable<{ organizationId: string, userId: string }, { success: boolean }>(this.firebase.functions, 'removeUserFromOrganization');
    const result = await removeUserFromOrganizationCallable({ organizationId, userId });

    return result.data.success;
  }
}
